import { useState, useEffect } from "react";
import { Icon } from "./Icon.jsx";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

export function ReviewForm({ productId, customerId, storeId, onReviewAdded }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [canReview, setCanReview] = useState(false);

  useEffect(() => {
    // Check if customer has purchased this product
    const checkEligibility = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/orders/customer/${customerId}`, { headers: { "x-customer-id": customerId } });
        if (res.ok) {
          const orders = await res.json();
          const hasPurchased = orders.some(order => 
            order.items && Array.isArray(order.items) && 
            order.items.some(item => item.id === productId)
          );
          setCanReview(hasPurchased);
        }
      } catch (e) {
        console.error("Error checking review eligibility:", e);
      }
    };

    if (customerId && productId) {
      checkEligibility();
    }
  }, [customerId, productId]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      setError("Maximum 5 photos allowed");
      return;
    }

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        setError("Only image files allowed");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be under 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    }
    
    e.target.value = "";
  };

  const handleRemovePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canReview) {
      setError("You must purchase this product to leave a review");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess(false);

    try {
      const reviewId = `review_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const res = await fetch(`${BACKEND_URL}/api/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: reviewId,
          product_id: productId,
          customer_id: customerId,
          rating: parseInt(rating),
          review_text: text || null,
          photos: photos.length > 0 ? photos : null,
          order_id: "pending",
          store_id: storeId || "",
          helpful_count: 0,
        })
      });

      if (res.ok) {
        setSuccess(true);
        setText("");
        setRating(5);
        setPhotos([]);
        setTimeout(() => {
          setSuccess(false);
          onReviewAdded?.();
        }, 2000);
      } else {
        const err = await res.json();
        setError(err.error || "Failed to submit review");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canReview) {
    return (
      <div style={{padding:"20px", background:"var(--surface)", borderRadius:"4px", marginBottom:"24px", fontSize:"13px", color:"var(--ink-soft)"}}>
        <p style={{margin:0}}><Icon n="edit" size="1.1em" inline /> Sign in and purchase this product to leave a review</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{padding:"20px", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"4px", marginBottom:"24px"}}>
      <h3 style={{fontSize:"14px", fontWeight:700, color:"var(--ink)", marginBottom:"16px", margin:"0 0 16px"}}>
        Share Your Review
      </h3>

      {error && (
        <div style={{padding:"12px", background:"rgba(255,80,80,0.1)", border:"1px solid rgba(255,80,80,0.3)", borderRadius:"8px", color:"rgba(255,80,80,0.85)", fontSize:"12px", marginBottom:"16px"}}>
          {error}
        </div>
      )}

      {success && (
        <div style={{padding:"12px", background:"rgba(68,204,102,0.1)", border:"1px solid rgba(68,204,102,0.3)", borderRadius:"8px", color:"rgba(68,204,102,0.85)", fontSize:"12px", marginBottom:"16px"}}>
          <Icon n="done" size="1.1em" inline /> Thank you for your review!
        </div>
      )}

      {/* Rating picker */}
      <div style={{marginBottom:"16px"}}>
        <label style={{display:"block", fontSize:"12px", fontWeight:600, color:"var(--ink)", marginBottom:"8px"}}>
          Your Rating:
        </label>
        <div style={{display:"flex", gap:"8px"}}>
          {[1,2,3,4,5].map(star => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              style={{fontSize:"28px", cursor:"pointer", opacity: star <= rating ? 1 : 0.3, transition:"opacity 0.2s", padding:0, border:"none", background:"none"}}
            >
              ★
            </button>
          ))}
        </div>
      </div>

      {/* Review text */}
      <div style={{marginBottom:"16px"}}>
        <label style={{display:"block", fontSize:"12px", fontWeight:600, color:"var(--ink)", marginBottom:"8px"}}>
          Your Review (optional):
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 1000))}
          placeholder="Share your experience with this product..."
          style={{width:"100%", minHeight:"100px", padding:"10px", border:"1px solid var(--border)", borderRadius:"8px", fontSize:"13px", fontFamily:"inherit", resize:"vertical"}}
        />
        <div style={{fontSize:"11px", color:"var(--ink-soft)", marginTop:"4px"}}>
          {text.length}/1000 characters
        </div>
      </div>

      {/* Photo upload */}
      <div style={{marginBottom:"16px"}}>
        <label style={{display:"block", fontSize:"12px", fontWeight:600, color:"var(--ink)", marginBottom:"8px"}}>
          Add Photos (up to 5):
        </label>
        <div style={{display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"12px"}}>
          {photos.map((photo, idx) => (
            <div key={idx} style={{position:"relative", width:"80px", height:"80px"}}>
              <img src={photo} style={{width:"100%", height:"100%", borderRadius:"8px", objectFit:"cover"}} alt={`Photo ${idx + 1}`} />
              <button
                type="button"
                onClick={() => handleRemovePhoto(idx)}
                style={{position:"absolute", top:"-8px", right:"-8px", width:"24px", height:"24px", borderRadius:"50%", background:"rgba(255,80,80,0.85)", color:"#fff", border:"none", cursor:"pointer", fontSize:"14px", fontWeight:700}}
              >
                ×
              </button>
            </div>
          ))}
          {photos.length < 5 && (
            <label style={{width:"80px", height:"80px", border:"2px dashed var(--border)", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.2s"}}>
              <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} style={{display:"none"}} />
              <span style={{fontSize:"24px", opacity:0.6}}>+</span>
            </label>
          )}
        </div>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={submitting}
        className="btn-pink"
        style={{width:"100%", padding:"12px", opacity: submitting ? 0.6 : 1, cursor: submitting ? "not-allowed" : "pointer"}}
      >
        {submitting ? "Submitting..." : "Post Review"}
      </button>
    </form>
  );
}
